import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { adminTeamsClient, adminUsersClient, groupsClient, groupUsersClient, groupInvitationsClient } from '~/services';

export const useGroupDetails = createGlobalState(() => {
    const toasted = useToasted();

    const group = ref({});
    const loading = ref(true);

    const catchRequestError = (error) => {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status == HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    };

    let onMountedGroupCallback = () => {};
    const onMountedGroup = (callback) => {
        onMountedGroupCallback = callback;
    };

    const mount = async (teamId, groupId) => {
        await nextTick();

        team.value = undefined;
        teamOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        orgUnit.value = undefined;
        customAttributes.value = undefined;

        if (!teamId || !groupId) {
            group.value = {};
            loading.value = false;
            onMountedGroupCallback?.();
            return;
        }

        try {
            group.value.group_id = groupId;
            group.value.team_id = teamId;
            loading.value = true;

            const response = await groupsClient.getGroup({ teamId }, groupId);
            group.value = response.data;
            orgUnit.value = response.data.org_unit || undefined;
            loading.value = false;

            if (group.value.team_id) {
                getTeam().then(() => {
                    if (team.value?.owner_id) {
                        getTeamOwner();
                    }
                });
            }

            onMountedGroupCallback?.();
        } catch (e) {
            catchRequestError(e);
            loading.value = false;
        }
    };

    const unmount = () => {
        group.value = {};
        team.value = undefined;
        teamOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        orgUnit.value = undefined;
        customAttributes.value = undefined;
        loading.value = false;
    };

    const refreshGroup = async (teamId, groupId) => {
        try {
            const response = await groupsClient.getGroup({ teamId }, groupId);
            group.value = response.data;
            orgUnit.value = response.data.org_unit || undefined;
        } catch (e) {
            console.error(e);
        }
    };

    const team = ref(undefined);
    const teamLoading = ref(false);
    const getTeam = async () => {
        if (!group.value.team_id) {
            return;
        }

        if (team.value || teamLoading.value) {
            return;
        }

        try {
            teamLoading.value = true;
            const response = await adminTeamsClient.getTeam(group.value.team_id);
            team.value = response.data;
            return response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamLoading.value = false;
        }
    };

    const teamOwner = ref(undefined);
    const teamOwnerLoading = ref(false);
    const getTeamOwner = async () => {
        if (!team.value?.owner_id) {
            return;
        }

        try {
            teamOwnerLoading.value = true;
            const response = await adminUsersClient.showUser(team.value.owner_id);
            teamOwner.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamOwnerLoading.value = false;
        }
    };

    const invitationsCount = ref(undefined);
    const invitationsCountLoading = ref(false);
    const getInvitationsCount = async () => {
        try {
            invitationsCountLoading.value = true;
            const response = await groupInvitationsClient.invitationsCount(group.value.team_id, group.value.group_id);
            invitationsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            invitationsCountLoading.value = false;
        }
    };

    const usersCount = ref(undefined);
    const usersCountLoading = ref(false);
    const getUsersCount = async () => {
        try {
            usersCountLoading.value = true;
            const response = await groupUsersClient.countGroupUsers(group.value.team_id, group.value.group_id);
            usersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            usersCountLoading.value = false;
        }
    };

    const orgUnit = ref(undefined);
    const orgUnitLoading = ref(false);

    const customAttributes = ref(undefined);
    const customAttributesLoading = ref(false);
    const getCustomAttributes = async () => {
        try {
            customAttributesLoading.value = true;
            const response = await groupsClient.getGroupCustomAttributes(
                { teamId: group.value.team_id },
                group.value.group_id
            );
            customAttributes.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            customAttributesLoading.value = false;
        }
    };

    return {
        loading,
        group,
        mount,
        unmount,
        refreshGroup,
        onMountedGroup,

        team,
        teamLoading,
        getTeam,

        teamOwner,
        teamOwnerLoading,
        getTeamOwner,

        invitationsCount,
        invitationsCountLoading,
        getInvitationsCount,

        usersCount,
        usersCountLoading,
        getUsersCount,

        orgUnit,
        orgUnitLoading,

        customAttributes,
        customAttributesLoading,
        getCustomAttributes,
    };
});
